import type { SelectionDataPipeline, SelectionStep } from "@/schemas/routing/selection-pipeline";

export const pipelineCategories = Object.freeze({
	filters: "filterSteps",
	sorts: "sortSteps",
	rules: "rulesSteps",
} as const);

export type PipelineCategoryKey = keyof typeof pipelineCategories;
export type PipelineCategoryName = (typeof pipelineCategories)[PipelineCategoryKey];

/**
 * Gets the steps array for a given category from a pipeline.
 * @param pipeline - The selection data pipeline
 * @param category - The category name (filterSteps, sortSteps, or rulesSteps)
 * @returns The array of steps for that category, or empty array if not found
 */
export const getStepsForCategory = (pipeline: SelectionDataPipeline, category: string): SelectionStep[] => {
	switch (category) {
		case pipelineCategories.filters:
			return pipeline.filterSteps;
		case pipelineCategories.sorts:
			return pipeline.sortSteps;
		case pipelineCategories.rules:
			return pipeline.rulesSteps;
		default:
			return [];
	}
};

/**
 * Moves an item in an array up or down by one position.
 * Mutates the original array and returns it.
 * @param arr - The array to modify
 * @param index - The current index of the item to move
 * @param moveDown - If true, moves item down (index + 1); if false, moves item up (index - 1)
 * @returns The modified array
 */
export const moveArrayItem = <T>(arr: T[], index: number, moveDown: boolean): T[] => {
	const newIndex = moveDown ? index + 1 : index - 1;
	if (newIndex < 0 || newIndex >= arr.length) {
		return arr;
	}
	arr.splice(newIndex, 0, arr.splice(index, 1)[0]);
	return arr;
};

export const buildEmptyPipeline = (name = ""): SelectionDataPipeline => ({
	name,
	filterSteps: [],
	sortSteps: [],
	rulesSteps: [],
});
